import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import MentionTag from '../../shared/mention-tag';
import WireCaption from '../../wire/wire-caption';
import RelativeTime from '../../wire/relative-time';
import WireTitle from './wire-title';
import NanoProfile from './nano-profile';
import WireSummary from '../../wire/wire-summary';
import WireFooter from './wire-footer';

const Container = styled.section`
  ${props => (props.theme.isMobile ? `
    margin: 0 -20px;
    padding-left: 20px;
    padding-right: 20px;
    padding-top: ${props.bordered ? 24 : 12}px;
    padding-bottom: ${props.bordered ? 14 : 7}px;

    ${p => !p.bordered && `
      &:first-of-type {
        padding-top: 24px;
      }

      &:last-of-type {
        padding-bottom: 14px;
      }
    `};
  ` : `
    margin: ${props.bordered ? '20px -26px 0px -26px' : '0px -26px'};
    padding-left: 26px;
    padding-right: 26px;
    padding-top: ${props.bordered ? 44 : 22}px;
    padding-bottom: ${props.bordered ? 30 : 15}px;

    ${p => !p.bordered && `
      &:first-of-type {
        padding-top: 44px;
      }

      &:last-of-type {
        padding-bottom: 30px;
      }
    `};
  `)};
  ${props => (!props.theme.isMobile && props.bordered) && `
    background: ${props.theme.secondaryColorLight7Lighten};
  `};
  border-radius: 0px;
  ${props => props.bordered && `
    border-radius: 4px;
  `};
  border: ${props => ((props.bordered && !props.theme.isMobile) ? '1px solid #e4eaee' : 'none')};

  .wire__summary > .clampped-summary {
    em {
      font-style: normal;
      font-weight: ${props => props.theme.fontWeightHeavy};
    }

    .show-more {
      font-weight: ${props => props.theme.fontWeightHeavy};
    }
  }
`;

const CaptionRow = styled.div`
  display: flex;
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: ${props => props.theme.fontWeightHeavy};
  ${props => (props.theme.isMobile ? `
    font-size: 10px;
    line-height: 18px;
  ` : `
    font-size: 12px;
    line-height: 12px;
  `)};
  text-transform: uppercase;
  justify-content: space-between;
  align-content: center;

  > span.main {
    color: ${props => props.theme.primaryColor};
  }
  span.secondary {
    color: ${props => props.theme.secondaryColor};
  }
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: -1em;
`;

export default function Wire({
  bordered, wire, showMention, position,
}) {
  return (
    <Container bordered={bordered}>
      <CaptionRow>
        <span className="main">
          <WireCaption wire={{ ...wire, timestamp: wire.firstPublishedAt }} />
        </span>
        <span className="wire__date secondary">
          <RelativeTime timestamp={wire.firstPublishedAt} useShortVersion={false} />
        </span>
      </CaptionRow>
      <WireTitle wire={wire} position={position} />
      <ProfileContainer>
        <NanoProfile profile={wire.profile} />
        {showMention && <MentionTag />}
      </ProfileContainer>
      <WireSummary
        allowFullSummary={false}
        summary={wire.summary}
        showMoreLink
        url={`/wires/${wire.slug}`}
        wireId={wire.id}
      />
      <WireFooter wire={wire} />
    </Container>
  );
}

Wire.propTypes = {
  wire: PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    summary: PropTypes.string.isRequired,
    firstPublishedAt: PropTypes.string.isRequired,
    isSponsored: PropTypes.bool.isRequired,
    commentCount: PropTypes.number.isRequired,
    likeCount: PropTypes.number.isRequired,
    liked: PropTypes.bool.isRequired,
    featuredCount: PropTypes.number.isRequired,
    userId: PropTypes.number.isRequired,
    editorsPicked: PropTypes.bool.isRequired,
    ownedByCurrentUser: PropTypes.bool.isRequired,
    category: PropTypes.shape({
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    series: PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
    profile: PropTypes.shape({
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      profilePictureUrl: PropTypes.string,
      contributorOrEditor: PropTypes.bool.isRequired,
    }).isRequired,
  }).isRequired,
  bordered: PropTypes.bool.isRequired,
  showMention: PropTypes.bool,
  position: PropTypes.number.isRequired,
};

Wire.defaultProps = {
  showMention: false,
};
